import { useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useForm, Controller } from 'react-hook-form';

// Components
import Grid from '@material-ui/core/Grid';
import SearchIcon from '@material-ui/icons/Search';
import TextField from 'components/CncUI/TextField';
import Dropdown from 'components/CncUI/Dropdown';
import Button from 'components/CncUI/Button';
import ClearCacheButton from './ClearCacheButton';
import ResetEmployeeButton from './ResetEmployeeButton';

// Utilities
import inputValidator from './inputValidator';

const useStyles = makeStyles(() => ({
  field: {
    width: 320,
  },
  button: {
    marginBottom: 22,
  },
}));

const SearchControls = props => {
  const classes = useStyles();
  const { onSearch, onClear, onUpdate, loading } = props;
  const {
    handleSubmit,
    register,
    errors,
    reset,
    watch,
    control,
    setValue,
    formState,
    trigger,
  } = useForm({
    mode: 'all',
    defaultValues: { idType: 'ssn', search: '' },
  });

  const search = watch('search');
  const idType = watch('idType');
  const searchDisabled = !formState.isValid;

  useEffect(() => {
    if (search && idType) onUpdate(search, idType);
  }, [search, idType]);

  useEffect(() => {
    if (search) trigger('search');
  }, [idType]);

  const submitForm = data => {
    onSearch(data.search);
  };

  const clearForm = () => {
    reset();
    onClear();
  };

  const onKeyDown = async e => {
    const { charCode, keyCode } = e || {};
    // Enter key 13
    if (charCode === 13 || keyCode === 13) {
      e.preventDefault();
      e.stopPropagation();
      if (!loading) handleSubmit(submitForm)();
    }
  };

  const idTypes = ['ssn', 'sin', 'email'].map(type => ({
    label: type.toUpperCase(),
    value: type,
  }));

  const maxLength =
    idType !== 'email'
      ? {
          InputProps: {
            inputProps: {
              maxLength: 11,
            },
          },
        }
      : {};

  return (
    <form onSubmit={handleSubmit(submitForm)}>
      <Grid container spacing={2}>
        <Grid
          item
          xs={8}
          container
          spacing={2}
          direction="row"
          alignItems="flex-end"
        >
          <Grid item>
            <Controller
              render={props => (
                <Dropdown
                  light
                  fullWidth
                  id="idType"
                  name="idType"
                  label="Search By"
                  value={props.value}
                  data={idTypes}
                  onChange={props.onChange}
                  inputRef={props.ref}
                  helperText={' '}
                  className={classes.dropdown}
                />
              )}
              name="idType"
              control={control}
            />
          </Grid>
          <Grid item>
            <TextField
              light
              fullWidth
              id="SearchField"
              name="search"
              label="Search"
              inputRef={register(inputValidator(idType, true))}
              onClear={() => setValue('search', '')}
              startIcon={<SearchIcon />}
              onKeyDown={onKeyDown}
              error={Boolean(errors.search)}
              helperText={(errors.search && errors.search.message) || ' '}
              className={classes.field}
              {...maxLength}
            />
          </Grid>
          <Grid item>
            <Button
              loading={loading}
              disabled={searchDisabled}
              id="SearchButton"
              className={classes.button}
              type="submit"
            >
              Search
            </Button>
          </Grid>
          <Grid item>
            <Button
              secondary
              id="ClearButton"
              onClick={clearForm}
              className={classes.button}
            >
              Clear Results
            </Button>
          </Grid>
        </Grid>
        <Grid
          item
          xs={4}
          container
          spacing={2}
          direction="row"
          alignItems="flex-end"
          justify="flex-end"
        >
          <Grid item>
            <ResetEmployeeButton
              searchValue={search}
              idType={idType}
              onSuccess={() => reset()}
            />
          </Grid>
        </Grid>
        <Grid item xs={6} container direction="row" alignItems="flex-end">
          <Grid item>
            <ClearCacheButton searchValue={search} idType={idType} />
          </Grid>
        </Grid>
      </Grid>
    </form>
  );
};

export default SearchControls;
